import { api } from '@/core/lib/api'
import {
  buildAdminListQuery,
  type TAdminListParams,
} from '@/core/lib/table-query'

import type {
  TCreateLeagueRequest,
  TLeagueAdminOption,
  TLeagueContext,
  TLeagueDetail,
  TLeagueListResponse,
  TLeagueMatch,
  TLeagueStanding,
  TUpdateLeagueRequest,
} from '@/modules/league/league.types'

const base = '/admin/leagues'

export async function listLeagues(
  params: TAdminListParams
): Promise<TLeagueListResponse> {
  const response = await api.get<TLeagueListResponse>(
    `${base}?${buildAdminListQuery(params)}`
  )
  return response.data
}

export async function getLeague(
  id: string,
  group?: number
): Promise<TLeagueDetail> {
  const query = group != null ? `?group=${group}` : ''
  const response = await api.get<TLeagueDetail>(`${base}/${id}${query}`)
  return response.data
}

export async function createLeague(
  body: TCreateLeagueRequest
): Promise<TLeagueDetail> {
  const response = await api.post<TLeagueDetail>(base, body)
  return response.data
}

export async function updateLeague(
  id: string,
  body: TUpdateLeagueRequest
): Promise<TLeagueDetail> {
  const response = await api.patch<TLeagueDetail>(`${base}/${id}`, body)
  return response.data
}

export async function deleteLeague(id: string): Promise<void> {
  await api.delete(`${base}/${id}`)
}

export async function reassignLeague(id: string): Promise<TLeagueDetail> {
  const response = await api.post<TLeagueDetail>(`${base}/${id}/reassign`)
  return response.data
}

export async function activateLeague(id: string): Promise<TLeagueDetail> {
  const response = await api.post<TLeagueDetail>(`${base}/${id}/activate`)
  return response.data
}

export async function finishLeague(id: string): Promise<TLeagueDetail> {
  const response = await api.post<TLeagueDetail>(`${base}/${id}/finish`)
  return response.data
}

export async function setLeagueTeamScore(
  leagueId: string,
  teamId: string,
  score: number
): Promise<TLeagueDetail> {
  const response = await api.patch<TLeagueDetail>(
    `${base}/${leagueId}/teams/${teamId}/score`,
    { score }
  )
  return response.data
}

export async function getLeagueStandings(
  id: string
): Promise<TLeagueStanding[]> {
  const response = await api.get<TLeagueStanding[]>(`${base}/${id}/standings`)
  return response.data
}

export async function getLeagueMatches(id: string): Promise<TLeagueMatch[]> {
  const response = await api.get<TLeagueMatch[]>(`${base}/${id}/matches`)
  return response.data
}

export async function listLeagueAdminOptions(): Promise<TLeagueAdminOption[]> {
  const response = await api.get<TLeagueAdminOption[]>(`${base}/admin-options`)
  return response.data
}

export async function getLeagueContext(): Promise<TLeagueContext> {
  const response = await api.get<TLeagueContext>(`${base}/context`)
  return response.data
}

export async function activateLeagueMatch(
  leagueId: string,
  matchId: string
): Promise<TLeagueMatch> {
  const response = await api.post<TLeagueMatch>(
    `${base}/${leagueId}/matches/${matchId}/activate`
  )
  return response.data
}

export async function setLeagueMatchAutoStart(
  leagueId: string,
  matchId: string,
  enabled: boolean
): Promise<TLeagueMatch> {
  const response = await api.patch<TLeagueMatch>(
    `${base}/${leagueId}/matches/${matchId}/auto-start`,
    { auto_start_enabled: enabled }
  )
  return response.data
}

// Award a pending match by walkover (hükmen galip). Backend refuses active /
// completed matches; the armed auto-start timer is cancelled automatically.
export async function setLeagueMatchWalkover(
  leagueId: string,
  matchId: string,
  winnerTeamId: string
): Promise<TLeagueMatch> {
  const response = await api.post<TLeagueMatch>(
    `${base}/${leagueId}/matches/${matchId}/walkover`,
    { winner_team_id: winnerTeamId }
  )
  return response.data
}

export type TMatchDateConflict = {
  kind: 'league' | 'tournament'
  match_id: string
  label: string
  scheduled_at: string
  team_id: string
}

export async function updateLeagueMatchDate(
  leagueId: string,
  matchId: string,
  scheduledAt: string,
  force = false
): Promise<TLeagueMatch> {
  const response = await api.patch<TLeagueMatch>(
    `${base}/${leagueId}/matches/${matchId}/date`,
    { scheduled_at: scheduledAt, force }
  )
  return response.data
}
